import { makeAutoObservable } from "mobx";
import { message } from "antd";
import http from "../../services/http";

export default class AdminPendingPaymentsStore {
  data?: IPendingPaymentsResponse;
  item?: IPendingPayment;
  lookups?: ILookups;
  isLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  async findAll(values?: Record<string, any>) {
    this.isLoading = true;
    try {
      const result = await http.get("/pending-payments", { params: values });
      this.data = result.data;
    } catch (e) {
      message.error("Не удалось загрузить список платежей");
    } finally {
      this.isLoading = false;
    }
  }

  async findOne(id: string) {
    this.isLoading = true;
    try {
      const result = await http.get(`/pending-payments/${id}`);
      this.item = result.data.data;
    } catch {
      message.error("Не удалось загрузить информацию о платеже");
    } finally {
      this.isLoading = false;
    }
  }

  async findLookups() {
    this.isLoading = true;
    try {
      const result = await http.get("/pending-payments/create");
      this.lookups = result.data;
    } catch {
      message.error("Не удалось загрузить справочники");
    } finally {
      this.isLoading = false;
    }
  }

  async onCreate(data: CreatePendingPaymentData) {
    this.isLoading = true;
    try {
      const result = await http.post("/pending-payments", data);
      message.success("Платеж успешно создан");
      return result.data.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Не удалось создать платеж";
      message.error(errorMessage);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  async onDelete(id: string) {
    this.isLoading = true;
    try {
      await http.delete(`/pending-payments/${id}`);
      message.success("Платеж успешно удален");
      
      if (this.data?.data) {
        this.data.data = this.data.data.filter(payment => payment.id !== id);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Не удалось удалить платеж";
      message.error(errorMessage);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  clear() {
    this.data = undefined;
    this.item = undefined;
    this.lookups = undefined;
  }
}

export interface CreatePendingPaymentData {
  userId: string;
  tariffId: string;
  licenseId: string;
  amount: number;
  transactionId?: string;
  description?: string;
  createdAt?: string;
}

export interface IPendingPaymentsResponse {
  data: IPendingPayment[];
  links?: {
    first: string;
    last: string;
    prev?: string | null;
    next?: string | null;
  };
  meta?: {
    current_page: number;
    from: number;
    last_page: number;
    path: string;
    per_page: number;
    total_amount: number;
    to: number;
    total: number;
    links: Array<{
      url?: string | null;
      label: string;
      active: boolean;
    }>;
  };
}

export interface IPendingPayment {
  id: string;
  amount: number;
  currency: string;
  transactionId?: string;
  status: {
    value: string;
    title: string;
  };
  isManual: boolean;
  description?: string;
  metadata?: Record<string, any>;
  createdAt: Date;
  user: IUser;
  tariff?: ITariff;
  featurePrice?: IFeaturePrice;
  license?: ILicense;
}

export interface IUser {
  id: string;
  name: string;
  email: string;
  profile?: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
  };
}

export interface ITariff {
  id: string;
  name: string;
  price?: number;
}

export interface ILicense {
  id: string;
  key: string;
  expiresAt?: string;
  isActive?: boolean;
}

export interface IFeature {
  code: string;
  title: string;
  description?: string;
  unit?: string;
  type?: string;
}

export interface IFeaturePrice {
  id: string;
  feature?: IFeature;
  price: number;
  oldPrice?: number;
}

export interface ILookups {
  tariffs: ITariff[];
  paymentsStatuses: [
    {
      value: 'string';
      title: 'string';
    }
  ];
  features: [
    {
      value: 'string';
      title: 'string';
    }
  ];
}
